import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Globe2 } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  ConfidenceBar,
  MetricCard,
} from '@/components';
import { frameworkInFocus, useActiveFocus, useData, inFocus } from '@/data/stores';
import { JDL_LEVELS } from '@/domain';
import { fmtCompact, fmtRelative } from '@/lib/format';
import { severityColor, severityLabel } from '@/lib/color';
import './dashboard.css';

export function NationalCOP() {
  const all = useData();
  const navigate = useNavigate();
  const focus = useActiveFocus();
  // Apply the active focus mode across all surfaces of the COP. The user's
  // chosen focus reshapes which assets, alerts, threat actors, and reports
  // appear here - without changing the data on disk.
  const data = useMemo(() => {
    const assets = all.assets.filter((a) => inFocus(focus, a));
    const assetIds = new Set(assets.map((a) => a.asset_id));
    const alerts = all.alerts.filter((al) =>
      al.asset_ids.some((id) => assetIds.has(id)),
    );
    const reports = all.reports.filter(
      (r) =>
        r.related_asset_ids.length === 0 ||
        r.related_asset_ids.some((id) => assetIds.has(id)),
    );
    const actors = focus.emphasis.threat_actors.length === 0
      ? all.actors
      : all.actors.filter((a) => focus.emphasis.threat_actors.includes(a.actor_id));
    const compliance = focus.applicable_frameworks.length === 0
      ? all.compliance
      : all.compliance.filter((c) => frameworkInFocus(focus, c));
    return {
      assets,
      alerts,
      reports,
      actors,
      compliance,
      vessels: all.vessels,
      otDevices: all.otDevices,
    };
  }, [all, focus]);

  const sectorHealth = useMemo(() => {
    const map = new Map<string, { total: number; ops: number; deg: number; off: number }>();
    for (const a of data.assets) {
      const cur = map.get(a.sector) ?? { total: 0, ops: 0, deg: 0, off: 0 };
      cur.total += 1;
      if (a.operational_status === 'operational') cur.ops += 1;
      if (a.operational_status === 'degraded') cur.deg += 1;
      if (a.operational_status === 'offline') cur.off += 1;
      map.set(a.sector, cur);
    }
    return Array.from(map.entries()).sort((a, b) => b[1].total - a[1].total);
  }, [data.assets]);

  const topAlerts = useMemo(() => {
    return [...data.alerts]
      .filter((a) => a.status !== 'resolved' && a.status !== 'dismissed')
      .sort((a, b) => b.severity - a.severity || b.detected_at.localeCompare(a.detected_at))
      .slice(0, 6);
  }, [data.alerts]);

  const topReports = useMemo(() => data.reports.slice(0, 5), [data.reports]);
  const topActors = useMemo(
    () => [...data.actors].sort((a, b) => b.threat_level - a.threat_level).slice(0, 4),
    [data.actors],
  );

  const populationAtRisk = useMemo(() => {
    const sectorMul: Record<string, number> = {
      energy_generation: 250000, energy_transmission: 600000, energy_distribution: 200000,
      oil_gas: 100000, water: 250000, wastewater: 200000, maritime_ports: 50000,
      telecommunications: 1500000, transport: 80000, manufacturing: 8000,
      data_centres: 500000, defence: 0, government: 1000000, mining: 5000, other: 0,
    };
    const ids = new Set<string>();
    for (const a of data.alerts) {
      if (a.severity < 4) continue;
      if (a.status === 'resolved' || a.status === 'dismissed') continue;
      a.asset_ids.forEach((id) => ids.add(id));
    }
    let total = 0;
    for (const id of ids) {
      const asset = data.assets.find((x) => x.asset_id === id);
      if (!asset) continue;
      const base = sectorMul[asset.sector] ?? 1000;
      const crit = [0.2, 0.4, 0.7, 1.0, 1.6][asset.criticality_rating - 1] ?? 1;
      total += base * crit * 0.4;
    }
    return Math.round(total);
  }, [data.alerts, data.assets]);

  return (
    <div className="iris-page iris-cop">
      <header className="iris-page-header">
        <div>
          <h1>National Common Operating Picture</h1>
          <p className="caption">
            Aggregate situational awareness across all CNI sectors. Click into any panel to
            drill down.
          </p>
        </div>
        <div className="iris-page-actions">
          <Button icon={<Globe2 size={14} strokeWidth={1.5} />} onClick={() => navigate('/map')}>
            Open Geospatial COP
          </Button>
        </div>
      </header>

      <section className="iris-page-metrics">
        <MetricCard label="Assets monitored" value={data.assets.length} />
        <MetricCard
          label="Sites with active alerts"
          value={
            new Set(
              data.alerts
                .filter((a) => a.status !== 'resolved' && a.status !== 'dismissed')
                .flatMap((a) => a.asset_ids),
            ).size
          }
          status="var(--status-degraded)"
        />
        <MetricCard
          label="Critical alerts"
          value={data.alerts.filter((a) => a.severity === 5 && a.status !== 'resolved').length}
          status="var(--status-critical)"
        />
        <MetricCard label="Vessels tracked" value={data.vessels.length} />
        <MetricCard label="OT devices" value={data.otDevices.length} />
        <MetricCard
          label="Population at risk"
          value={fmtCompact(populationAtRisk)}
          caption="from current high-severity alerts"
        />
      </section>

      <div className="iris-cop-grid">
        <Card
          title="Sector health"
          subtitle="Operational vs degraded vs offline asset mix per sector"
        >
          <ul className="iris-sector-list">
            {sectorHealth.map(([sector, m]) => {
              const opsPct = (m.ops / m.total) * 100;
              return (
                <li key={sector}>
                  <div className="iris-sector-row-top">
                    <span>{sector.replace(/_/g, ' ')}</span>
                    <span className="caption mono">
                      {m.ops}/{m.total}
                    </span>
                  </div>
                  <div className="iris-sector-bar">
                    <div
                      className="iris-sector-fill"
                      style={{
                        width: `${opsPct}%`,
                        background:
                          opsPct >= 90
                            ? 'var(--status-operational)'
                            : opsPct >= 75
                              ? 'var(--status-degraded)'
                              : 'var(--status-critical)',
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card
          title="Top open alerts"
          actions={
            <Button
              size="sm"
              icon={<ArrowRight size={12} strokeWidth={1.5} />}
              onClick={() => navigate('/alerts')}
            >
              All alerts
            </Button>
          }
        >
          <ul className="iris-cop-alerts">
            {topAlerts.map((a) => (
              <li
                key={a.alert_id}
                onClick={() => navigate(`/alerts/${a.alert_id}`)}
                style={{ borderLeftColor: severityColor(a.severity) }}
              >
                <div className="iris-cop-alert-row">
                  <span style={{ color: severityColor(a.severity) }}>
                    ● {severityLabel(a.severity)}
                  </span>
                  <span className="caption">{fmtRelative(a.detected_at)}</span>
                </div>
                <div>{a.title}</div>
                <div className="caption mono">{a.asset_ids.join(', ')}</div>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Active threat campaigns">
          <ul className="iris-cop-actors">
            {topActors.map((a) => (
              <li key={a.actor_id} className="iris-cop-actor">
                <header>
                  <span>{a.name}</span>
                  <Badge
                    color={
                      a.threat_level === 5
                        ? 'var(--status-critical)'
                        : a.threat_level === 4
                          ? 'var(--status-degraded)'
                          : 'var(--status-info)'
                    }
                  >
                    THREAT {a.threat_level}/5
                  </Badge>
                </header>
                <div className="caption">{a.attribution}</div>
                <div className="caption">Targets: {a.target_sectors.slice(0, 4).join(', ')}</div>
              </li>
            ))}
          </ul>
        </Card>

        <Card
          title="Latest intelligence reports"
          actions={
            <Button
              size="sm"
              icon={<ArrowRight size={12} strokeWidth={1.5} />}
              onClick={() => navigate('/intel')}
            >
              KG view
            </Button>
          }
        >
          <ul className="iris-cop-reports">
            {topReports.map((r) => (
              <li key={r.report_id}>
                <header>
                  <span>{r.title}</span>
                  <Badge color="var(--text-secondary)">{r.source}</Badge>
                </header>
                <div className="caption iris-cop-report-meta">
                  <span>{fmtRelative(r.timestamp)}</span>
                  <ConfidenceBar value={r.confidence.composite} />
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="JDL fusion (live)" subtitle="Levels 0-4 across all data feeds">
          <ul className="iris-cop-fusion">
            {JDL_LEVELS.map((l) => (
              <li key={l.level}>
                <span className="iris-cop-fusion-dot" style={{ background: l.health === 'green' ? 'var(--status-operational)' : 'var(--status-degraded)' }} />
                <div>
                  <div>{l.label}</div>
                  <div className="caption">{l.current_activity}</div>
                </div>
                <span className="mono caption">{l.throughput_per_min}/min</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Compliance posture" subtitle="Headline scores per framework">
          <ul className="iris-cop-compliance">
            {data.compliance.map((f) => (
              <li key={f.framework_id}>
                <span>{f.framework_id.replace(/_/g, ' ')}</span>
                <ConfidenceBar value={f.overall_score} />
              </li>
            ))}
          </ul>
          <Button
            size="sm"
            icon={<ArrowRight size={12} strokeWidth={1.5} />}
            onClick={() => navigate('/compliance')}
            style={{ marginTop: 8 }}
          >
            Open compliance
          </Button>
        </Card>
      </div>
    </div>
  );
}
